import { PROJECTIONS_FEATURE_FLAG } from "components/projections/featureFlags";
import * as Sentry from "@sentry/react";
import { Outlet, Route, Routes } from "react-router";

import { useTabName } from "components/common/TabTitle";

import {
  RedirectToTenant,
  TenantIndexRedirect,
} from "components/settings/tenant";
import { SentryUserTracker } from "components/SentryUserTracker";
import { SignInPage } from "components/signIn";
import { SignInCompletePage } from "components/signInComplete";
import { LinkAccountPage } from "components/linkAccount";
import { RecipeComplianceArtefactPage } from "./render/RecipeComplianceArtefact";
import { RecipeCompositionArtefactPage } from "./render/RecipeCompositionArtefact";
import { NotFound } from "components/notFound";

import { AdminRoutes } from "./admin";
import {
  RequireAdminPage,
  RequireOverviewAnyTenantPage,
  RequireAnalyticsAccess,
  RequireFeatureFlagsPage,
  RequirePermissions,
  RequirePermissionsPage,
  RequireManager,
  RequireTenant,
  RequireUser,
  ValidateUser,
} from "./authentication";
import { CraneRoutes } from "./crane";
import { TenantRoutes } from "./tenant";
import { LabellingToolRoutes } from "./tool";
import { DashboardLayout } from "src/dashboard/DashboardLayout";
import { OverviewPage } from "components/overview/OverviewPage";
import { ProvideAvailableTenantMetadata } from "contexts/availableTenantMetadata";
import { SelectTenantPage } from "components/settings/SelectTenantPage";
import { TenantGroupRedirect } from "components/common/TenantGroupRedirect";
import { RailRouteMapPage } from "components/railRouteMap/RailRouteMapPage";
import { ConsumptionProjectionsPage } from "components/projections/consumption/ProjectionsPage";
import { MissionControlTabbedPage } from "components/missionControl/MissionControlTabbedPage";
import { DeliveryAdjustmentsPage } from "components/projections/delivery-adjustments/DeliveryAdjustmentsPage";
import { PlanningRoutes } from "./planning";
import { StrategyDashboardPage } from "components/strategyDashboard/StrategyDashboardPage";
import { DemoPage } from "components/demo/DemoPage";
import { TenantLabellingCoveragePage } from "components/labellingCoverage/TenantLabellingCoveragePage";
import { LabellingCoverageOverviewPage } from "components/labellingCoverage/LabellingCoverageOverviewPage";
import { ManageOrganisationUsers } from "components/organisationUsers/manage";
import { LabellingCostOverviewPage } from "components/labellingCost/LabellingCostOverviewPage";
import { ManageLabellersOverviewPage } from "components/manageLabellersOverview/ManageLabellersOverviewPage";
import { ReviewerTrackingOverviewPage } from "components/reviewerTracking/ReviewerTrackingOverviewPage";

const SentryRoutes = Sentry.wrapReactRouterRouting(Routes);

export const AppRoutes = () => {
  useTabName();

  return (
    <>
      <SentryUserTracker />
      <SentryRoutes>
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-in/complete" element={<SignInCompletePage />} />
        <Route path="/link-account" element={<LinkAccountPage />} />
        <Route
          path="/:tenant/render/recipe-compliance"
          element={
            <RequireUser>
              <ValidateUser>
                <RequireTenant>
                  <RequirePermissions
                    permissions={["render_artefact", "clark_user"]}
                    fallback={<NotFound />}
                  >
                    <RecipeComplianceArtefactPage />
                  </RequirePermissions>
                </RequireTenant>
              </ValidateUser>
            </RequireUser>
          }
        />
        <Route
          path="/:tenant/render/recipe-composition"
          element={
            <RequireUser>
              <ValidateUser>
                <RequireTenant>
                  <RequirePermissions
                    permissions={["render_artefact", "clark_user"]}
                    fallback={<NotFound />}
                  >
                    <RecipeCompositionArtefactPage />
                  </RequirePermissions>
                </RequireTenant>
              </ValidateUser>
            </RequireUser>
          }
        />
        <Route
          path="/"
          element={
            <RequireUser>
              <RedirectToTenant />
            </RequireUser>
          }
        />
        <Route
          path="/mission-control"
          element={
            <RequireUser>
              <RequireOverviewAnyTenantPage>
                <DashboardLayout>
                  <Outlet />
                </DashboardLayout>
              </RequireOverviewAnyTenantPage>
            </RequireUser>
          }
        >
          <Route
            index
            element={
              <TenantGroupRedirect
                basePath="/mission-control"
                fallback={<SelectTenantPage />}
              />
            }
          />
          <Route path=":tenantGroup" element={<MissionControlTabbedPage />} />
        </Route>
        <Route
          path="/logistics"
          element={
            <RequireUser>
              <RequireAdminPage>
                <DashboardLayout>
                  <Outlet />
                </DashboardLayout>
              </RequireAdminPage>
            </RequireUser>
          }
        >
          <Route
            index
            element={
              <TenantGroupRedirect
                basePath="/logistics"
                fallback={<RailRouteMapPage />}
              />
            }
          />
          <Route path=":tenantGroup" element={<RailRouteMapPage />} />
        </Route>
        <Route
          path="/scrap-eye"
          element={
            <RequireUser>
              <ProvideAvailableTenantMetadata>
                <DashboardLayout>
                  <Outlet />
                </DashboardLayout>
              </ProvideAvailableTenantMetadata>
            </RequireUser>
          }
        >
          <Route
            path="labelling-coverage"
            element={
              <RequireAdminPage>
                <Outlet />
              </RequireAdminPage>
            }
          >
            <Route index element={<LabellingCoverageOverviewPage />} />
            <Route
              path=":tenantName"
              element={<TenantLabellingCoveragePage />}
            />
          </Route>
          <Route path="labelling-cost">
            <Route
              index
              element={
                <RequireAdminPage>
                  <LabellingCostOverviewPage />
                </RequireAdminPage>
              }
            />
          </Route>
          <Route path="manage-labellers">
            <Route
              index
              element={
                <RequireAdminPage>
                  <ManageLabellersOverviewPage />
                </RequireAdminPage>
              }
            />
          </Route>
          <Route path="review-data">
            <Route
              index
              element={
                <RequireAdminPage>
                  <ReviewerTrackingOverviewPage />
                </RequireAdminPage>
              }
            />
          </Route>
        </Route>
        <Route
          path="/o/:organisationName"
          element={
            <RequireUser>
              <RequireAdminPage>
                <DashboardLayout>
                  <Outlet />
                </DashboardLayout>
              </RequireAdminPage>
            </RequireUser>
          }
        >
          <Route path="admin/users" element={<ManageOrganisationUsers />} />
        </Route>
        <Route
          path="/:tenant/*"
          element={
            <RequireUser>
              <ValidateUser>
                <RequireTenant>
                  <DashboardLayout>
                    <Routes>
                      <Route
                        path="/live/demo"
                        element={
                          <RequireAdminPage>
                            <DemoPage />
                          </RequireAdminPage>
                        }
                      />
                      <Route path="/tool/*" element={<LabellingToolRoutes />} />
                      <Route path="/crane/*" element={<CraneRoutes />} />
                      <Route
                        path="/dashboard/:tabKey?"
                        element={
                          <RequireAnalyticsAccess>
                            <StrategyDashboardPage />
                          </RequireAnalyticsAccess>
                        }
                      />

                      <Route
                        index
                        element={
                          <TenantIndexRedirect>
                            <OverviewPage />
                          </TenantIndexRedirect>
                        }
                      />
                      <Route
                        path="*"
                        element={
                          <>
                            <Routes>
                              <Route
                                path="/projections/*"
                                element={
                                  <RequireFeatureFlagsPage
                                    featureFlags={[PROJECTIONS_FEATURE_FLAG]}
                                  >
                                    <Routes>
                                      <Route
                                        path="/consumption"
                                        element={
                                          <RequireManager>
                                            <ConsumptionProjectionsPage />
                                          </RequireManager>
                                        }
                                      />
                                      <Route
                                        path="/delivery-adjustments"
                                        element={
                                          <RequireManager>
                                            <DeliveryAdjustmentsPage />
                                          </RequireManager>
                                        }
                                      />
                                      <Route path="*" element={<NotFound />} />
                                    </Routes>
                                  </RequireFeatureFlagsPage>
                                }
                              />
                              <Route
                                path="/planning/*"
                                element={
                                  <RequireFeatureFlagsPage
                                    featureFlags={["optimiser"]}
                                  >
                                    <RequirePermissionsPage
                                      permissions={["scenario_planning"]}
                                    >
                                      <PlanningRoutes />
                                    </RequirePermissionsPage>
                                  </RequireFeatureFlagsPage>
                                }
                              />
                              <Route
                                path="/admin/*"
                                element={<AdminRoutes />}
                              />
                              <Route path="/*" element={<TenantRoutes />} />
                            </Routes>
                          </>
                        }
                      />
                    </Routes>
                  </DashboardLayout>
                </RequireTenant>
              </ValidateUser>
            </RequireUser>
          }
        />
      </SentryRoutes>
    </>
  );
};
